// src/pages/TrustControlPage.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Shield, Upload, Users, Globe, RefreshCw, Trash2,
  ExternalLink, Plus, X, ChevronDown, Search,
  Edit3, Clipboard, MessageSquare, AlertTriangle, CheckCircle,
} from "lucide-react";
import useTrustStore from "../../store/useTrustStore";
import BackgroundFooter from "../BackgroundFooter";
import NavigationBar from "../Nav";
import { Col, Row } from "react-bootstrap";
import Menubar from "../Menubar";
import { logActivity } from '../../utils/activityLogApi';

const BRAND = {
  primary: "#6BCE8B", primaryDark: "#4EAD6E", primaryLight: "#E8F8EE",
  secondary: "#035EA5", secondaryDark: "#024A82", secondaryLight: "#E6F0F9",
  white: "#FFFFFF",
  gray50: "#F8FAFC", gray100: "#F1F5F9", gray200: "#E2E8F0",
  gray300: "#CBD5E1", gray400: "#94A3B8", gray500: "#64748B",
  gray600: "#475569", gray900: "#0F172A",
};
const FONT = "'Inter', sans-serif";

const statusStyles = {
  PASS:    { bg: "#DEF7EC", text: "#03543F", label: "Pass" },
  PARTIAL: { bg: "#FEF9C3", text: "#92400E", label: "Partial" },
  GAP:     { bg: "#FDE8E8", text: "#9B1C1C", label: "Gap" },
  NA:      { bg: BRAND.gray100, text: BRAND.gray500, label: "N/A" },
};
const sevStyles = {
  CRITICAL: { bg: "#FDE8E8", text: "#991B1B" },
  HIGH: { bg: "#FEE2E2", text: "#DC2626" },
  MEDIUM: { bg: "#FEF9C3", text: "#A16207" },
  LOW: { bg: BRAND.primaryLight, text: BRAND.primaryDark },
};

const obsTypeStyles = {
  OBSERVATION:    { bg: BRAND.secondaryLight, text: BRAND.secondary, label: "Observation" },
  FINDING:        { bg: "#FDE8E8", text: "#991B1B", label: "Finding" },
  RECOMMENDATION: { bg: BRAND.primaryLight, text: BRAND.primaryDark, label: "Recommendation" },
  COMMENT:        { bg: BRAND.gray100, text: BRAND.gray500, label: "Comment" },
};
const obsStatusStyles = {
  OPEN:        { bg: "#FEF9C3", text: "#A16207", label: "Open" },
  IN_PROGRESS: { bg: BRAND.secondaryLight, text: BRAND.secondary, label: "In Progress" },
  RESOLVED:    { bg: BRAND.primaryLight, text: BRAND.primaryDark, label: "Resolved" },
  CLOSED:      { bg: BRAND.gray100, text: BRAND.gray500, label: "Closed" },
};


/* ═══ STATUS DROPDOWN (Portal) ═══ */
function StatusDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const current = statusStyles[value] || statusStyles.NA;

// Remove these from SecurityModel.jsx — they're in the wrong file:

  const openMenu = useCallback(() => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right });
    }
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target) &&
          menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <>
      <button ref={btnRef} onClick={openMenu} style={{
        padding: "4px 14px", borderRadius: "6px", fontSize: "12px",
        fontWeight: 600, fontFamily: FONT, cursor: "pointer",
        background: current.bg, color: current.text,
        border: "none", display: "inline-flex", alignItems: "center",
        gap: "4px", minWidth: "76px", justifyContent: "center",
      }}>
        {current.label} <ChevronDown size={11} />
      </button>
      {open && ReactDOM.createPortal(
        <div ref={menuRef} style={{
          position: "fixed", top: pos.top, left: pos.left - 110,
          width: "110px", background: BRAND.white,
          border: `1px solid ${BRAND.gray200}`, borderRadius: "8px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)", zIndex: 99999, overflow: "hidden",
        }}>
          {Object.entries(statusStyles).map(([key, s]) => (
            <button key={key}
              onClick={() => { onChange(key); setOpen(false); }}
              style={{
                display: "block", width: "100%", padding: "8px 14px",
                fontSize: "12px", fontWeight: 600, fontFamily: FONT,
                background: value === key ? s.bg : "transparent",
                color: s.text, border: "none", cursor: "pointer", textAlign: "left",
              }}
              onMouseEnter={(e) => { if (value !== key) e.currentTarget.style.background = BRAND.gray50; }}
              onMouseLeave={(e) => { if (value !== key) e.currentTarget.style.background = "transparent"; }}>
              {s.label}
            </button>
          ))}
        </div>, document.body
      )}
    </>
  );
}


/* ═══ MODAL ═══ */
function Modal({ title, show, onClose, children, wide }) {
  if (!show) return null;
  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", inset: 0, background: "rgba(3,94,165,0.18)",
      backdropFilter: "blur(4px)", display: "flex", alignItems: "center",
      justifyContent: "center", zIndex: 100000, padding: "20px",
    }}>
      <div style={{
        background: BRAND.white, borderRadius: "14px", width: "100%",
        maxWidth: wide ? "520px" : "440px",
        boxShadow: "0 25px 60px rgba(3,94,165,0.2)", overflow: "hidden",
      }}>
        <div style={{
          padding: "14px 18px", borderBottom: `1px solid ${BRAND.gray200}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: BRAND.gray900, fontFamily: FONT }}>{title}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}>
            <X size={16} style={{ color: BRAND.gray400 }} />
          </button>
        </div>
        <div style={{ padding: "18px" }}>{children}</div>
      </div>
    </div>, document.body
  );
}


const inputStyle = {
  width: "100%", padding: "9px 12px", border: `1px solid ${BRAND.gray200}`,
  borderRadius: "8px", fontSize: "13px", fontFamily: FONT, outline: "none", boxSizing: "border-box",
};
const btnPrimary = {
  padding: "7px 16px", borderRadius: "8px", fontSize: "12px", fontWeight: 600,
  fontFamily: FONT, cursor: "pointer", border: "none",
  background: BRAND.secondary, color: BRAND.white, display: "inline-flex",
  alignItems: "center", gap: "5px",
};
const btnOutline = {
  ...btnPrimary, background: BRAND.white, color: BRAND.gray600,
  border: `1px solid ${BRAND.gray200}`,
};


/* ═══ HELPER ═══ */
const extractDomain = (email) => {
  if (!email || !email.includes("@")) return "";
  return email.split("@")[1].toLowerCase();
};


/* ═══════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════ */
export default function TrustControlPage({ userData }) {
  const { controlId } = useParams();
  const {
    selectedControl, loading, fetchControlDetail, saveQuestionResponse,
    addPolicy, removePolicy, addEvidence, removeEvidence,
    updateOwnership, addRiskMapping, removeRiskMapping,
    addRiskTreatment, removeRiskTreatment, recalculateScoring, setProfile,
    addObservation, updateObservation, removeObservation,
  } = useTrustStore();
  const { profile } = useAuth();
const orgId = profile?.orgId;
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() =>
    JSON.parse(localStorage.getItem("isSidebarCollapsed")) || false
  );

  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [evidenceForm, setEvidenceForm] = useState({ evidenceName: "", type: "", notes: "", file: null });
 const [showPolicyModal, setShowPolicyModal] = useState(false);
 const [policyUploading, setPolicyUploading] = useState(false);
const [policyForm, setPolicyForm] = useState({
  policyName: "", version: "1.0", notes: "", file: null,
});
const [expandedPolicy, setExpandedPolicy] = useState(null); // for version history toggle

  const [showRiskModal, setShowRiskModal] = useState(false);
  const [riskForm, setRiskForm] = useState({ riskId: "", riskName: "", severity: "" });
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [ownerModalType, setOwnerModalType] = useState("owner"); // "owner" | "contributor"
  const [ownerRole, setOwnerRole] = useState("controlOwner");

  // ── Auditor Observations ──
  const [showObservationModal, setShowObservationModal] = useState(false);
  const [observationForm, setObservationForm] = useState({
    title: "", type: "OBSERVATION", severity: "MEDIUM", status: "OPEN", description: "", recommendation: "",
  });
  const [editingObservation, setEditingObservation] = useState(null); // observationId or null
  const [expandedObservation, setExpandedObservation] = useState(null); // observationId or null

  // ── Domain users for ownership picker ──
  const [allDomainUsers, setAllDomainUsers] = useState([]);
  const [loadingAllUsers, setLoadingAllUsers] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
const [expandedRisks, setExpandedRisks] = useState({});
const [expandedTreatments, setExpandedTreatments] = useState({});
const [treatingRiskId, setTreatingRiskId] = useState(null);
const [treatmentAction, setTreatmentAction] = useState('');
const [treatmentNotes, setTreatmentNotes] = useState('');
  useEffect(() => {
    if (profile) { setProfile(profile); fetchControlDetail(controlId); }
  }, [profile, controlId]);
const navigate = useNavigate();
  const fetchAllDomainUsers = async () => {
    setLoadingAllUsers(true);
    try {
      const currentUserEmail = profile?.email || "";
      const currentDomain = extractDomain(currentUserEmail);
      const region = sessionStorage.getItem("userRegion") || "AP";
      const apiUrl = `https://iffud7s7y3.execute-api.ap-southeast-2.amazonaws.com/prod/api/org/${profile.orgId}/users`;
      const response = await axios.get(apiUrl, { headers: { Region: region } });
      const users = response.data
        .filter(user => {
          const userDomain = extractDomain(user.email);
          return userDomain === currentDomain && user.email !== currentUserEmail;
        })
        .map(user => ({ ...user }));
      setAllDomainUsers(users);
    } catch (error) {
      console.error("Failed to load domain users:", error);
    } finally {
      setLoadingAllUsers(false);
    }
  };

 const openOwnerModal = (type) => {
  setOwnerModalType(type);            // you can keep or drop this
  setOwnerRole("controlOwner");       // default selection
  setUserSearch("");
  setSelectedUser(null);
  setShowOwnerModal(true);
  if (allDomainUsers.length === 0) fetchAllDomainUsers();
};


const handleAssignUser = async () => {
  if (!selectedUser) return;
  const ctrl = (selectedControl?.metadata || {}).controlId;
  if (!ctrl) return;

  const payload = ownerRole === "contributor"
    ? { addContributor: selectedUser.email }
    : { [ownerRole]: selectedUser.email };

  console.log(">>> Assigning ownership:", ctrl, payload);
  await updateOwnership(ctrl, payload);

  // Force re-fetch and log result
  const { selectedControl: updated } = useTrustStore.getState();
  console.log(">>> After refresh, ownership:", updated?.ownership);

  setShowOwnerModal(false);
  setSelectedUser(null);
  setUserSearch("");
};



  const filteredUsers = allDomainUsers.filter(u => {
    const q = userSearch.toLowerCase();
    return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  const data = selectedControl;
  if (!data) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        background: BRAND.gray50, fontFamily: FONT,
        gap: "16px",
      }}>
        <div style={{
          width: "48px", height: "48px", borderRadius: "50%",
          border: `3px solid ${BRAND.gray200}`,
          borderTopColor: BRAND.primary,
          animation: "spin 0.8s linear infinite",
        }} />
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: "14px", fontWeight: 600, color: BRAND.gray900, margin: "0 0 4px" }}>
            Loading Control Details
          </p>
          <p style={{ fontSize: "12px", color: BRAND.gray400, margin: 0 }}>
            Fetching {controlId} data...
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }


  const meta = data.metadata || {};
  const questions = data.questions || [];
  const policies = data.policies || [];
  const evidence = data.evidence || [];
  const ownership = data.ownership || {};
  const riskMapping = data.riskMapping || {};
  const riskMappings = riskMapping.mappings || [];
  const riskTreatments = data.riskTreatments || [];
  const fwMapping = data.frameworkMapping || {};
  const frameworkMappings = fwMapping.mappings || [];
  const fwSource = fwMapping.source || "";
  const scoring = data.scoring || {};
  const maturityValue = scoring.controlMaturity || "0%";
  const sev = sevStyles[meta.severity] || { bg: BRAND.gray100, text: BRAND.gray500 };
  const observations = data.observations || [];

const uniqueRiskMappings = riskMappings.filter(
  (r, i, arr) => arr.findIndex(x => x.riskId === r.riskId) === i
);

const uniqueRiskTreatments = riskTreatments.filter(
  (t, i, arr) => arr.findIndex(x => x.treatmentId === t.treatmentId) === i
);
  const cardStyle = {
    background: BRAND.white, border: `1px solid ${BRAND.gray200}`,
    borderRadius: "10px", marginBottom: "16px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.03)", fontFamily: FONT,
  };
  const cardHead = (title, right) => (
    <div style={{ padding: "10px 16px", borderBottom: `1px solid ${BRAND.gray200}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: BRAND.gray900 }}>{title}</h3>
      {right}
    </div>
  );
  const tHead = (...cols) => (
    <div style={{ display: "flex", padding: "7px 16px", background: BRAND.gray50, borderBottom: `1px solid ${BRAND.gray200}`, fontSize: "11px", fontWeight: 600, color: BRAND.gray500, textTransform: "uppercase", letterSpacing: "0.4px" }}>
      {cols.map((c, i) => <div key={i} style={{ width: c.w || "auto", flex: c.flex || "none", textAlign: c.align || "left" }}>{c.label}</div>)}
    </div>
  );

  return (
    <Row className="g-0">
      <Col xs={12}
        className={`bg-transparent sidebar-container ${isSidebarCollapsed ? "collapsed" : "hello"}`}
        style={{ width: isSidebarCollapsed ? "90px" : "317px", transition: "width 0.3s ease" }}>
        <Menubar isCollapsed={isSidebarCollapsed} onToggle={() => setIsSidebarCollapsed(p => !p)} />
      </Col>

      <Col className="pt-0 px-0 ps-0 section-container"
        style={{ width: `calc(100% - ${isSidebarCollapsed ? "90px" : "317px"})`, transition: "width 0.3s ease" }}>
        <NavigationBar userData={userData} isSidebarCollapsed={isSidebarCollapsed} />
        <Col className="overflow-auto p-0" style={{ height: "calc(100vh - 60px)" }}>
          <div style={{ background: BRAND.white, color: BRAND.gray900, fontFamily: FONT, minHeight: "100%" }}>
            <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "20px 24px" }}>

              <Link to={`/trust/domain/${meta.domain}`} style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                fontSize: "13px", color: BRAND.secondary, textDecoration: "none",
                marginBottom: "12px", fontWeight: 500,
              }}>
                <ArrowLeft size={15} /> Back to {meta.domainName}
              </Link>

              
              <h1 style={{ margin: "0 0 2px", fontSize: "22px", fontWeight: 700, color: BRAND.gray900 }}>{meta.controlName}</h1>
              <p style={{ fontSize: "13px", color: BRAND.gray500, margin: "0 0 16px" }}>Domain: {meta.domain} — {meta.domainName}</p>

              {/* Header strip */}
              <div style={{ ...cardStyle, padding: "14px 18px", marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px" }}>
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "13px", fontFamily: "monospace", color: BRAND.secondary, fontWeight: 600 }}>{meta.controlId}</span>
                  <span style={{ padding: "3px 10px", fontSize: "11px", borderRadius: "6px", fontWeight: 600, background: sev.bg, color: sev.text }}>{meta.severity}</span>
                  <span style={{ padding: "3px 10px", fontSize: "11px", borderRadius: "6px", fontWeight: 500, background: BRAND.secondaryLight, color: BRAND.secondary }}>{meta.category}</span>
                  {fwSource && <span style={{ padding: "3px 10px", fontSize: "10px", borderRadius: "6px", background: BRAND.gray100, color: BRAND.gray500 }}>{fwSource === "CONTROL_LEVEL" ? "Control mappings" : "Domain inherited"}</span>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "3px" }}>
                  <span style={{ fontSize: "11px", color: BRAND.gray400 }}>Control maturity</span>
                  <span style={{ fontSize: "26px", fontWeight: 800, color: BRAND.secondary, lineHeight: 1 }}>{maturityValue}</span>
                  <button onClick={() => recalculateScoring(meta.controlId)}
                    style={{ background: "none", border: `1px solid ${BRAND.gray200}`, borderRadius: "6px", padding: "3px 10px", fontSize: "11px", color: BRAND.gray500, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", fontFamily: FONT, marginTop: "2px" }}>
                    <RefreshCw size={11} /> Recalculate
                  </button>
                </div>
              </div>

              {/* ══ TWO COLUMNS ══ */}
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px" }}>

                {/* ─── LEFT ─── */}
                <div>
                  {/* Description */}
                  {/* <div style={cardStyle}>
                    {cardHead("Description")}
                    <div style={{ padding: "14px 16px", fontSize: "13px", lineHeight: 1.6, color: BRAND.gray900 }}>
                      {meta.description || meta.question || "No description."}
                    </div>
                  </div> */}

                  {/* Questions */}
                  <div style={cardStyle}>
                    {cardHead("Applicability (SOA)", <span style={{ fontSize: "11px", color: BRAND.gray400 }}>Status (Pass / Gap)</span>)}
                    {tHead({ label: "Question", flex: 1 }, { label: "Status", w: "100px", align: "right" })}
                    {questions.map((q, i) => (
                      <div key={q.questionId} style={{ display: "flex", alignItems: "center", padding: "9px 16px", borderBottom: i === questions.length - 1 ? "none" : `1px solid ${BRAND.gray100}`, fontSize: "13px" }}>
                        <div style={{ flex: 1, color: BRAND.gray900 }}>{q.question}</div>
                        <div style={{ width: "100px", display: "flex", justifyContent: "flex-end" }}>
                          <StatusDropdown value={q.response || "NA"} onChange={(s) => saveQuestionResponse(meta.controlId, q.questionId, s, q.notes || "")} />
                        </div>
                      </div>
                    ))}
                    {questions.length === 0 && <div style={{ padding: "14px 16px", color: BRAND.gray400 }}>No questions configured.</div>}
                  </div>

                  {/* Policies */}
                 {/* Policies — Upload with Version Control */}
<div style={cardStyle}>
  {cardHead(
    "Policies",
    <button
      onClick={() => {
        setPolicyForm({ policyName: "", version: "1.0", notes: "", file: null });
        setShowPolicyModal(true);
      }}
      style={btnPrimary}
    >
     Upload Policy
    </button>
  )}
  {/* Table Header */}
  {tHead(
    { label: "Policy", flex: 1 },
    { label: "Version", w: "80px", align: "center" },
    { label: "Uploaded By", w: "150px" },
    { label: "Date", w: "100px" },
    { label: "", w: "80px", align: "right" },
  )}

  {policies.map((p, i) => (
    <div key={p.policyId}>
      {/* Main row */}
      <div
        style={{
          display: "flex", alignItems: "center", padding: "9px 16px",
          borderBottom: (i < policies.length - 1 && expandedPolicy !== p.policyId) ? `1px solid ${BRAND.gray100}` : "none",
          fontSize: "13px", cursor: "pointer",
        }}
        onClick={() => setExpandedPolicy(expandedPolicy === p.policyId ? null : p.policyId)}
      >
        <div style={{ flex: 1, color: BRAND.gray900, display: "flex", alignItems: "center", gap: "8px" }}>
          {/* File icon */}
          <div style={{
            width: "28px", height: "28px", borderRadius: "6px",
            background: BRAND.secondaryLight, display: "flex",
            alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <Shield size={13} style={{ color: BRAND.secondary }} />
          </div>
          <div>
            <div style={{ fontWeight: 500 }}>{p.policyName}</div>
            {p.notes && (
              <div style={{ fontSize: "11px", color: BRAND.gray400, marginTop: "1px" }}>
                {p.notes}
              </div>
            )}
          </div>
        </div>

        <div style={{
          width: "80px", textAlign: "center",
        }}>
          <span style={{
            padding: "2px 10px", borderRadius: "10px", fontSize: "11px",
            fontWeight: 600, background: BRAND.primaryLight,
            color: BRAND.primaryDark, border: `1px solid ${BRAND.primary}30`,
          }}>
            v{p.version || "1.0"}
          </span>
        </div>

        <div style={{ width: "150px", color: BRAND.gray500, fontSize: "12px" }}>
          {p.owner || p.uploadedBy || "—"}
        </div>

        <div style={{ width: "100px", color: BRAND.gray500, fontSize: "12px" }}>
          {p.uploadedAt?.slice(0, 10) || "—"}
        </div>

        <div style={{ width: "80px", display: "flex", justifyContent: "flex-end", gap: "6px" }}>
          {/* Download */}
          {p.fileKey && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const region = sessionStorage.getItem("userRegion") || "IN";
                  const res = await axios.get(
                    `https://api.srqllabapi.com/trustdownloadpolicy/api/trust/control/${meta.controlId}/policy/download`,
                    { params: { fileKey: p.fileKey }, headers: { "X-Region": region, "X-Org-Id": profile.orgId } }
                  );
                  
                  window.open(res.data.downloadUrl, "_blank");
                  logActivity({
                        userData,
                        orgId:       orgId,
                        region:      sessionStorage.getItem('userRegion') || 'AP',
                        module:      'Global Compliance & Trust',
                        category:    'control',
                        action:      'Policy downloaded',
                        severity:    'success',
                        description: `${p.policyName} Policy is downloaded`,});
                } catch (err) { console.error("Failed to download policy", err); }
              }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              <ExternalLink size={14} style={{ color: BRAND.gray400 }} />
            </button>
          )}

          {/* New Version */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              const nextVersion = (parseFloat(p.version || "1.0") + 0.1).toFixed(1);
              setPolicyForm({ policyName: p.policyName, version: nextVersion, notes: "", file: null, parentPolicyId: p.policyId });
              setShowPolicyModal(true);
            }}
            title="Upload new version"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <Plus size={14} style={{ color: BRAND.secondary }} />
          </button>

         {/* Delete */}
<button
  onClick={async (e) => {
    e.stopPropagation();
    if (p.fileKey) {
      try {
        const region = sessionStorage.getItem("userRegion") || "IN";
        await axios.delete(
          `https://api.srqllabapi.com/trustdeletepolicy/api/trust/control/${meta.controlId}/policy/${p.policyId}/file?fileKey=${encodeURIComponent(p.fileKey)}`,
          { headers: { "X-Region": region, "X-Org-Id": profile.orgId } }
        );
        logActivity({
                        userData,
                        orgId:       orgId,
                        region:      sessionStorage.getItem('userRegion') || 'AP',
                        module:      'Global Compliance & Trust',
                        category:    'control',
                        action:      'Policy deleted',
                        severity:    'success',
                        description: `${p.policyName} Policy is deleted`,});
      } catch (err) { console.error("Failed to delete policy file", err); }
    }
    await removePolicy(meta.controlId, p.policyId);
    await fetchControlDetail(controlId);
  }}
  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
>
  <Trash2 size={14} style={{ color: BRAND.gray300 }} />
</button>

        </div>
      </div>

      {/* Expanded: Version History */}
      {expandedPolicy === p.policyId && p.versionHistory?.length > 0 && (
        <div style={{
          background: BRAND.gray50, borderBottom: `1px solid ${BRAND.gray100}`,
          padding: "8px 16px 8px 52px",
        }}>
          <div style={{ fontSize: "11px", fontWeight: 600, color: BRAND.gray500, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.3px" }}>
            Version History
          </div>
          {p.versionHistory.map((vh, vi) => (
            <div key={vi} style={{
              display: "flex", alignItems: "center", gap: "10px",
              padding: "5px 0", fontSize: "12px",
              borderBottom: vi < p.versionHistory.length - 1 ? `1px solid ${BRAND.gray100}` : "none",
            }}>
              <span style={{
                padding: "1px 8px", borderRadius: "8px", fontSize: "10px",
                fontWeight: 600, background: BRAND.gray100, color: BRAND.gray500,
              }}>
                v{vh.version}
              </span>
              <span style={{ color: BRAND.gray600, flex: 1 }}>{vh.uploadedBy || "—"}</span>
              <span style={{ color: BRAND.gray400 }}>{vh.uploadedAt?.slice(0, 10) || "—"}</span>
              {vh.fileKey && (
                <button
                  onClick={async () => {
                    try {
                      const region = sessionStorage.getItem("userRegion") || "IN";
                      const res = await axios.get(
                        `https://api.srqllabapi.com/trustdownloadpolicy/api/trust/control/${meta.controlId}/policy/download`,
                        { params: { fileKey: vh.fileKey }, headers: { "X-Region": region, "X-Org-Id": profile.orgId } }
                      );
                      window.open(res.data.downloadUrl, "_blank");
                    } catch (err) { console.error(err); }
                  }}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  <ExternalLink size={12} style={{ color: BRAND.gray400 }} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  ))}

  {policies.length === 0 && (
    <div style={{ padding: "30px 16px", textAlign: "center", color: BRAND.gray400 }}>
      <Upload size={24} style={{ color: BRAND.gray300, marginBottom: "8px" }} />
      <div style={{ fontSize: "13px" }}>No policies uploaded yet.</div>
      <div style={{ fontSize: "11px", marginTop: "2px" }}>Upload your first policy document to get started.</div>
    </div>
  )}
</div>


                  {/* Evidence */}
                  <div style={cardStyle}>
                    {cardHead("Evidence", <button onClick={() => setShowEvidenceModal(true)} style={btnPrimary}>Upload evidence</button>)}
                    {tHead({ label: "Evidence", flex: 1 }, { label: "uploded by", w: "220px" }, { label: "Date", w: "100px" }, { label: "", w: "50px" })}
                    {evidence.map((e, i) => (
                      <div key={e.evidenceId} style={{ display: "flex", alignItems: "center", padding: "9px 16px", borderBottom: i === evidence.length - 1 ? "none" : `1px solid ${BRAND.gray100}`, fontSize: "13px" }}>
                        <div style={{ flex: 1, color: BRAND.gray900 }}>{e.evidenceName}</div>
                        <div style={{ width: "150px", color: BRAND.gray500 }}>{e.uploadedBy || "-"}</div>
                        <div style={{ width: "100px", color: BRAND.gray500 }}>{e.uploadedAt?.slice(0, 10) || "-"}</div>
                        <div style={{ width: "50px", display: "flex", justifyContent: "flex-end", gap: "4px" }}>
                         
{e.fileKey && (
  <button onClick={async () => {
    try {
      const region = sessionStorage.getItem("userRegion") || "IN";
      const res = await axios.get(
        `https://api.srqllabapi.com/trustgetevidence/api/trust/control/${meta.controlId}/evidence/download`,
        {
          params: { fileKey: e.fileKey },
          headers: { "X-Region": region, "X-Org-Id": profile.orgId },
        }
      );
      window.open(res.data.downloadUrl, "_blank");
      logActivity({
                        userData,
                        orgId:       orgId,
                        region:      sessionStorage.getItem('userRegion') || 'AP',
                        module:      'Global Compliance & Trust',
                        category:    'control',
                        action:      'Evidence downloaded',
                        severity:    'success',
                        description: `${e.evidenceName} evidence is downloaded`,});
    } catch (err) {
      console.error("Failed to get download URL:", err);
    }
  }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
    <ExternalLink size={14} style={{ color: BRAND.gray400 }} />
  </button>
)}

                        
<button onClick={async () => {
  if (e.fileKey) {
    const region = sessionStorage.getItem("userRegion") || "IN";
    try {
      await axios.delete(
        `https://api.srqllabapi.com/trustdeleteevidence/api/trust/control/${meta.controlId}/evidence/${e.evidenceId}/file?fileKey=${encodeURIComponent(e.fileKey)}`,
        { headers: { "X-Region": region, "X-Org-Id": profile.orgId } }
      );
      await fetchControlDetail(controlId);
       logActivity({
                        userData,
                        orgId:       orgId,
                        region:      sessionStorage.getItem('userRegion') || 'AP',
                        module:      'Global Compliance & Trust',
                        category:    'control',
                        action:      'Evidence deleted',
                        severity:    'warning',
                        description: `${e.evidenceName} evidence is deleted`,});
    } catch (err) {
      console.error("Failed to delete evidence file:", err);
    }
  } else {
    await removeEvidence(meta.controlId, e.evidenceId);
  }
}} style={{ background: "none", border: "none", cursor: "pointer" }}>
  <Trash2 size={14} style={{ color: BRAND.gray300 }} />
</button>

                        </div>
                      </div>
                    ))}
                    {evidence.length === 0 && <div style={{ padding: "14px 16px", color: BRAND.gray400 }}>No evidence uploaded.</div>}
                  </div>

                  {/* ══ AUDITOR OBSERVATIONS ══ */}
                  <div style={cardStyle}>
                    {cardHead(
                      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Auditor Observations
                        {observations.length > 0 && (
                          <span style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            minWidth: "20px", height: "20px", borderRadius: "10px",
                            background: BRAND.secondaryLight, color: BRAND.secondary,
                            fontSize: "11px", fontWeight: 700, padding: "0 6px",
                          }}>
                            {observations.length}
                          </span>
                        )}
                      </span>,
                      <button
                        onClick={() => {
                          setObservationForm({ title: "", type: "OBSERVATION", severity: "MEDIUM", status: "OPEN", description: "", recommendation: "" });
                          setEditingObservation(null);
                          setShowObservationModal(true);
                        }}
                        style={btnPrimary}
                      >
                        <Plus size={13} /> Add Observation
                      </button>
                    )}

                    {tHead(
                      { label: "Type", w: "80px" },
                      { label: "Title", flex: 1 },
                      { label: "Severity", w: "90px" },
                      { label: "Date", w: "100px" },
                      { label: "Actions", w: "60px", align: "right" },
                    )}

                    {observations.map((obs, i) => {
                      const typeStyle = obsTypeStyles[obs.type] || obsTypeStyles.OBSERVATION;
                      const obsSev = sevStyles[obs.severity] || { bg: BRAND.gray100, text: BRAND.gray500 };
                      const isExpanded = expandedObservation === obs.observationId;
                      const obsStatus = obsStatusStyles[obs.status] || obsStatusStyles.OPEN;

                      return (
                        <div key={obs.observationId}>
                          {/* Main row */}
                          <div
                            style={{
                              display: "flex", alignItems: "center", padding: "9px 16px",
                              borderBottom: (i < observations.length - 1 && !isExpanded) ? `1px solid ${BRAND.gray100}` : "none",
                              fontSize: "13px", cursor: "pointer",
                              transition: "background 0.1s",
                            }}
                            onClick={() => setExpandedObservation(isExpanded ? null : obs.observationId)}
                            onMouseEnter={(e) => { e.currentTarget.style.background = BRAND.gray50; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                          >
                            {/* Type badge */}
                            <div style={{ width: "80px" }}>
                              <span style={{
                                padding: "2px 8px", borderRadius: "6px", fontSize: "10px",
                                fontWeight: 600, background: typeStyle.bg, color: typeStyle.text,
                                whiteSpace: "nowrap",
                              }}>
                                {typeStyle.label}
                              </span>
                            </div>

                            {/* Title */}
                            <div style={{ flex: 1, color: BRAND.gray900, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: "8px" }}>
                              {obs.title || "—"}
                            </div>

                            {/* Severity badge */}
                            <div style={{ width: "90px" }}>
                              <span style={{
                                padding: "2px 8px", borderRadius: "6px", fontSize: "10px",
                                fontWeight: 600, background: obsSev.bg, color: obsSev.text,
                              }}>
                                {obs.severity || "—"}
                              </span>
                            </div>

                            {/* Date */}
                            <div style={{ width: "100px", color: BRAND.gray500, fontSize: "12px" }}>
                              {obs.addedAt?.slice(0, 10) || obs.createdAt?.slice(0, 10) || "—"}
                            </div>

                            {/* Actions */}
                            <div style={{ width: "60px", display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setObservationForm({
                                    title: obs.title || "",
                                    type: obs.type || "OBSERVATION",
                                    severity: obs.severity || "MEDIUM",
                                    status: obs.status || "OPEN",
                                    description: obs.description || "",
                                    recommendation: obs.recommendation || "",
                                  });
                                  setEditingObservation(obs.observationId);
                                  setShowObservationModal(true);
                                }}
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                                title="Edit observation"
                              >
                                <Edit3 size={14} style={{ color: BRAND.gray400 }} />
                              </button>
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  await removeObservation(meta.controlId, obs.observationId);
                                }}
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                                title="Delete observation"
                              >
                                <Trash2 size={14} style={{ color: BRAND.gray300 }} />
                              </button>
                            </div>
                          </div>

                          {/* Expanded detail panel */}
                          {isExpanded && (
                            <div style={{
                              background: BRAND.gray50,
                              borderBottom: i < observations.length - 1 ? `1px solid ${BRAND.gray100}` : "none",
                              padding: "12px 16px 14px 16px",
                            }}>
                              {/* Description */}
                              {obs.description && (
                                <div style={{ marginBottom: "10px" }}>
                                  <div style={{ fontSize: "11px", fontWeight: 600, color: BRAND.gray500, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "4px" }}>
                                    Description
                                  </div>
                                  <div style={{ fontSize: "13px", color: BRAND.gray900, lineHeight: 1.6 }}>
                                    {obs.description}
                                  </div>
                                </div>
                              )}

                              {/* Recommendation */}
                              {obs.recommendation && (
                                <div style={{ marginBottom: "10px" }}>
                                  <div style={{ fontSize: "11px", fontWeight: 600, color: BRAND.gray500, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "4px" }}>
                                    Recommendation
                                  </div>
                                  <div style={{ fontSize: "13px", color: BRAND.gray900, lineHeight: 1.6 }}>
                                    {obs.recommendation}
                                  </div>
                                </div>
                              )}

                              {/* Metadata row */}
                              <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", marginTop: "6px" }}>
                                {obs.addedBy && (
                                  <span style={{ fontSize: "11px", color: BRAND.gray500 }}>
                                    <span style={{ fontWeight: 600 }}>Added by:</span> {obs.addedBy}
                                  </span>
                                )}
                                {obs.updatedAt && (
                                  <span style={{ fontSize: "11px", color: BRAND.gray500 }}>
                                    <span style={{ fontWeight: 600 }}>Updated:</span> {obs.updatedAt?.slice(0, 10)}
                                  </span>
                                )}
                                {obs.status && (
                                  <span style={{
                                    padding: "2px 10px", borderRadius: "6px", fontSize: "11px",
                                    fontWeight: 600, background: obsStatus.bg, color: obsStatus.text,
                                  }}>
                                    {obsStatus.label}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {observations.length === 0 && (
                      <div style={{ padding: "30px 16px", textAlign: "center", color: BRAND.gray400 }}>
                        <Clipboard size={24} style={{ color: BRAND.gray300, marginBottom: "8px" }} />
                        <div style={{ fontSize: "13px" }}>No auditor observations yet.</div>
                        <div style={{ fontSize: "11px", marginTop: "2px" }}>Add an observation, finding, or recommendation for this control.</div>
                      </div>
                    )}
                  </div>

                  {/* Ownership */}
                  <div style={cardStyle}>
                   <div style={cardStyle}>
  {cardHead("Ownership",
    <button
      style={btnPrimary}
      onClick={() => openOwnerModal("manage")}
    >
     Manage Ownership
    </button>
  )}
</div>

                    <div style={{ padding: "12px 16px", display: "grid", gridTemplateColumns: "160px 1fr", gap: "8px 16px", fontSize: "13px" }}>
                     {[
  ["Control Owner", ownership.controlOwner],
  ["Operational Owner", ownership.operationalOwner],
  ["Reviewer", ownership.reviewer],
  ["Contributor", typeof ownership.contributors === "string"
    ? ownership.contributors.replace(/,/g, ", ")
    : Array.isArray(ownership.contributors)
      ? ownership.contributors.join(", ")
      : ""],
].map(([l, v]) => (
  <React.Fragment key={l}>
    <span style={{ color: BRAND.gray500, fontWeight: 500 }}>{l}</span>
    <span style={{ color: BRAND.gray900 }}>{v || "Not assigned"}</span>
  </React.Fragment>
))}

                    </div>
                  </div>
                </div>

                {/* ─── RIGHT ─── */}
                <div>
                  {/* ══ LINKED ASSET CATEGORIES ══ */}
<div style={cardStyle}>
  {cardHead(
    <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      Linked Asset Categories
      
    </span>
  )}

  <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: "8px" }}>
    {[
      { label: "Hardware",          color: BRAND.secondary,    bg: BRAND.secondaryLight,  key: "Hardware" },
      { label: "Software",          color: "#7C3AED",           bg: "#EDE9FE",             key: "Software" },
      { label: "Data / Information", color: "#0369A1",           bg: "#E0F2FE",             key: "Data / Information" },
      { label: "Network",          color: BRAND.primaryDark,   bg: BRAND.primaryLight,    key: "Network" },
      { label: "People",           color: "#B45309",           bg: "#FEF3C7",             key: "People" },
      { label: "Physical",         color: BRAND.gray600,       bg: BRAND.gray100,         key: "Physical" },
      { label: "Cloud Service",     color: "#0E7490",           bg: "#CFFAFE",             key: "Cloud Service" },
      { label: "Other",             color: BRAND.gray500,       bg: BRAND.gray100,         key: "Other" },
    ].map(cat => {
      // Count how many assets in this category
      // Replace `assets` with your actual assets prop/state if available
      // For now wiring to meta.linkedAssetCategories if present
      // ✅ Derive directly from riskMappings — no backend change needed
       const linkedCats = [...new Set(
       uniqueRiskMappings
        .map(r => r.assetCategory)
        .filter(Boolean)
        )];
      const isLinked = linkedCats.includes(cat.key);
      const riskCount = uniqueRiskMappings.filter(r => r.assetCategory === cat.key).length;

      return (
        <div
          key={cat.key}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 12px", borderRadius: "8px",
            border: `1px solid ${isLinked ? cat.color + "40" : BRAND.gray100}`,
            background: isLinked ? cat.bg : BRAND.gray50,
            opacity: isLinked ? 1 : 0.5,
            transition: "all 0.15s",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "16px" }}>{cat.icon}</span>
            <span style={{
              fontSize: "13px", fontWeight: isLinked ? 600 : 400,
              color: isLinked ? cat.color : BRAND.gray400,
            }}>
              {cat.label}
            </span>
          </div>

          {isLinked && (
    <span style={{
        fontSize: "11px", fontWeight: 700,
        padding: "2px 8px", borderRadius: "10px",
        background: cat.color + "15", color: cat.color,
        border: `1px solid ${cat.color}30`,
    }}>
        {riskCount > 0 ? `${riskCount} risk${riskCount > 1 ? "s" : ""}` : "Linked"}
    </span>
)}
        </div>
      );
    })}
  </div>
</div>
                  {/* Compliance Coverage */}
                  <div style={cardStyle}>
                    {cardHead("Applicable Compliance")}
                    <div style={{ padding: "12px 16px", maxHeight: "260px", overflowY: "auto" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {frameworkMappings.length > 0 ? frameworkMappings.map((fm, i) => (
                          <span key={i} style={{
                            padding: "5px 14px", borderRadius: "20px", fontSize: "12px",
                            fontWeight: 500, whiteSpace: "nowrap",
                            background: BRAND.gray50, border: `1px solid ${BRAND.gray200}`, color: BRAND.gray900,
                          }}>{fm.framework}</span>
                        )) : <span style={{ color: BRAND.gray400 }}>No framework mappings.</span>}
                      </div>
                    </div>
                  </div>

           
           
{/* Linked Risks */}
<div style={cardStyle}>
 <div style={{ padding: "10px 16px", borderBottom: `1px solid ${BRAND.gray200}`, 
  display: "flex", alignItems: "center" }}>
    <span style={{ width: "60px", fontSize: "11px", color: BRAND.gray400, fontWeight: 600 }}>Risk ID</span>
    <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: BRAND.gray900, flex: 1 }}>Linked Risks</h3>
    <button
      onClick={() => setShowRiskModal(true)}
      style={btnOutline}
    >
      <Plus size={12} /> Link Risk
    </button>
</div>

<div style={{ padding: "12px 16px", maxHeight: "260px", overflowY: "auto" }}>
{uniqueRiskMappings.map((r, i) => {
  const isExpanded = expandedRisks[r.riskId];
  const isLong = (r.riskName || "").length > 60;
  const isTreating = treatingRiskId === r.riskId;

  return (
    
    <div key={r.riskId || i}>
      {/* Existing risk row — unchanged */}
      <div style={{
        display: "flex", alignItems: "flex-start", padding: "9px 16px",
        borderBottom: (!isTreating && i === uniqueRiskMappings.length - 1) ? "none" : `1px solid ${BRAND.gray100}`,
        fontSize: "13px", gap: "8px",
      }}>
       <span
  onClick={() => navigate(`/?riskId=${r.riskId}`)}
  style={{
    width: "60px", fontSize: "12px", fontWeight: 600,
    color: BRAND.secondary, flexShrink: 0, paddingTop: "1px",
    cursor: "pointer", textDecoration: "underline",
  }}
  title="View in risk dashboard"
>
  {r.riskId || "—"}
</span>

        <div style={{ flex: 1, color: BRAND.gray900 }}>
          <div style={{
            display: isExpanded ? "block" : "-webkit-box",
            WebkitLineClamp: isExpanded ? undefined : 2,
            WebkitBoxOrient: isExpanded ? undefined : "vertical",
            overflow: isExpanded ? "visible" : "hidden",
            lineHeight: 1.5,
          }}>
            {r.riskName || `Risk ${r.riskId}`}
          </div>

          <div style={{ display: "flex", gap: "6px", marginTop: "4px", flexWrap: "wrap", alignItems: "center" }}>
            {r.addedBy && (
              <span style={{ fontSize: "10px", color: BRAND.gray400 }}>
                👤 {r.addedBy}
              </span>
            )}
            {r.source && (
              <span style={{
                fontSize: "10px", padding: "1px 6px", borderRadius: "4px",
                background: r.source === "AI" ? BRAND.secondaryLight : BRAND.primaryLight,
                color: r.source === "AI" ? BRAND.secondary : BRAND.primaryDark,
                fontWeight: 600,
              }}>
                {r.source}
              </span>
            )}
            {r.severity && (
              <span style={{
                fontSize: "10px", padding: "1px 6px", borderRadius: "4px",
                background: r.severity === "Critical" ? "#FDE8E8"
                  : r.severity === "High" ? "#FEE2E2"
                  : r.severity === "Medium" ? "#FEF9C3"
                  : BRAND.primaryLight,
                color: r.severity === "Critical" ? "#991B1B"
                  : r.severity === "High" ? "#DC2626"
                  : r.severity === "Medium" ? "#A16207"
                  : BRAND.primaryDark,
                fontWeight: 600,
              }}>
                {r.severity}
              </span>
            )}
          </div>

          {isLong && (
            <button
              onClick={() => setExpandedRisks(prev => ({ ...prev, [r.riskId]: !isExpanded }))}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: "11px", fontWeight: 600, color: BRAND.secondary,
                padding: "2px 0", marginTop: "2px", fontFamily: FONT,
              }}
            >
              {isExpanded ? "Show less ↑" : "Show more ↓"}
            </button>
          )}
        </div>

        {/* ── NEW: Treat button ── */}
        <button
          onClick={() => {
            setTreatingRiskId(isTreating ? null : r.riskId);
            setTreatmentAction('');
            setTreatmentNotes('');
          }}
          style={{
            flexShrink: 0, padding: "3px 10px", borderRadius: "6px",
            fontSize: "11px", fontWeight: 600, cursor: "pointer",
            border: `1px solid ${isTreating ? BRAND.secondary : BRAND.gray200}`,
            background: isTreating ? BRAND.secondaryLight : BRAND.white,
            color: isTreating ? BRAND.secondary : BRAND.gray500,
          }}
        >
          {isTreating ? "Cancel" : "Treat"}
        </button>
      </div>

      {/* ── NEW: Inline treatment panel ── */}
      {isTreating && (
        <div style={{
          background: BRAND.gray50,
          borderBottom: i === uniqueRiskMappings.length - 1 ? "none" : `1px solid ${BRAND.gray100}`,
          padding: "12px 16px 14px 16px",
        }}>
          {/* Treatment type label */}
          <div style={{ fontSize: "11px", fontWeight: 600, color: BRAND.gray500, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: "8px" }}>
            Select treatment type
          </div>

          {/* 4 treatment options */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
            {[
              { value: "Mitigate", bg: "#E6F0F9", color: BRAND.secondary,    desc: "Reduce via controls" },
              { value: "Accept",   bg: "#FEF9C3", color: "#A16207",           desc: "Formally accept risk" },
             
              { value: "Avoid",    bg: "#FDE8E8", color: "#991B1B",           desc: "Eliminate the activity" },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setTreatmentAction(opt.value)}
                style={{
                  padding: "6px 12px", borderRadius: "8px", cursor: "pointer",
                  fontFamily: FONT, fontSize: "12px", fontWeight: 600,
                  border: `1.5px solid ${treatmentAction === opt.value ? opt.color : BRAND.gray200}`,
                  background: treatmentAction === opt.value ? opt.bg : BRAND.white,
                  color: treatmentAction === opt.value ? opt.color : BRAND.gray500,
                  transition: "all 0.15s",
                  textAlign: "left",
                }}
              >
                <div>{opt.value}</div>
                <div style={{ fontSize: "10px", fontWeight: 400, marginTop: "1px", opacity: 0.8 }}>{opt.desc}</div>
              </button>
            ))}
          </div>

          {/* Notes field */}
          <textarea
            placeholder="Justification or notes (optional)"
            value={treatmentNotes}
            rows={2}
            onChange={e => setTreatmentNotes(e.target.value)}
            style={{ ...inputStyle, marginBottom: "10px", resize: "vertical" }}
          />

          {/* Confirm button */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              disabled={!treatmentAction}
              onClick={async () => {
                await addRiskTreatment(meta.controlId, {
                  treatmentId: `RT-${r.riskId}-${meta.controlId}-${Date.now()}`,
                  treatment: treatmentNotes || r.riskName || "",
                  treatmentType: treatmentAction,
                  status: treatmentAction === "Accept"   ? "PARTIAL"
                        : treatmentAction === "Avoid"    ? "GAP"
                        : treatmentAction === "Transfer" ? "PARTIAL"
                        : "PASS",
                  riskId: r.riskId,
                  addedBy: profile?.email,
                });
                setTreatingRiskId(null);
                setTreatmentAction('');
                setTreatmentNotes('');
              }}
              style={{
                ...btnPrimary,
                opacity: !treatmentAction ? 0.5 : 1,
                cursor: !treatmentAction ? "not-allowed" : "pointer",
              }}
            >
              <CheckCircle size={13} /> Confirm Treatment
            </button>
          </div>
        </div>
      )}
    </div>
  );
})}


  {uniqueRiskMappings.length === 0 && <div style={{ padding: "14px 16px", color: BRAND.gray400, fontSize: "13px" }}>No risks mapped.</div>}
  </div>
</div>


{/* Risk Treatment */}
<div style={cardStyle}>
  <div style={{ padding: "10px 16px", borderBottom: `1px solid ${BRAND.gray200}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
    <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: BRAND.gray900 }}>Risk Treatment</h3>
    <span style={{ fontSize: "11px", color: BRAND.gray400, fontWeight: 600 }}>Status</span>
  </div>
  {uniqueRiskTreatments.map((t, i) => {
  const st = statusStyles[t.status] || statusStyles.PASS;
  const isExpanded = expandedTreatments[t.treatmentId];
  const isLong = (t.treatment || "").length > 80;

  return (
    <div key={t.treatmentId || i} style={{
      padding: "9px 16px",
      borderBottom: i === uniqueRiskTreatments.length - 1 ? "none" : `1px solid ${BRAND.gray100}`,
      fontSize: "13px",
    }}>
      {/* Top row — text + badges */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
        <div style={{ flex: 1, color: BRAND.gray900, lineHeight: 1.5 }}>
          <div style={{
           display: isExpanded ? "block" : "-webkit-box",
WebkitLineClamp: isExpanded ? undefined : 2,
WebkitBoxOrient: isExpanded ? undefined : "vertical",
overflow: isExpanded ? "visible" : "hidden",
          }}>
            {t.treatment}
          </div>
          {isLong && (
            <button
              onClick={() => setExpandedTreatments(prev => ({ ...prev, [t.treatmentId]: !isExpanded }))}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: "11px", fontWeight: 600, color: BRAND.secondary,
                padding: "2px 0", marginTop: "2px", fontFamily: FONT,
              }}
            >
              {isExpanded ? "Show less ↑" : "Show more ↓"}
            </button>
          )}
        </div>
        {/* Badges stay top-right */}
        <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
         <span style={{
  padding: "3px 10px", fontSize: "11px", borderRadius: "6px",
  fontWeight: 600,
  background:
    t.treatmentType === "Accept"  ? "#FEF9C3" :
    t.treatmentType === "Mitigate" ? "#E6F0F9" :
    t.treatmentType === "Transfer" ? BRAND.primaryLight :
    t.treatmentType === "Avoid"    ? "#FDE8E8" :
    "#E6F0F9",
  color:
    t.treatmentType === "Accept"  ? "#A16207" :
    t.treatmentType === "Mitigate" ? BRAND.secondary :
    t.treatmentType === "Transfer" ? BRAND.primaryDark :
    t.treatmentType === "Avoid"    ? "#991B1B" :
    BRAND.secondary,
}}>
  {t.treatmentType || "Mitigate"}
</span>
          <span style={{
            padding: "3px 10px", fontSize: "11px", borderRadius: "6px",
            fontWeight: 600, background: st.bg, color: st.text,
          }}>
            {t.status || "—"}
          </span>
        </div>
      </div>
    </div>
  );
})}
  {uniqueRiskTreatments.length === 0 && <div style={{ padding: "14px 16px", color: BRAND.gray400, fontSize: "13px" }}>No risk treatments.</div>}
</div>

                  {/* Control Status Summary */}
                  <div style={cardStyle}>
                    <div style={{ padding: "10px 16px", borderBottom: `1px solid ${BRAND.gray200}` }}>
                      <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: BRAND.gray900 }}>Control Status Summary</h3>
                    </div>
                    <div style={{ padding: "16px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 12px" }}>
                        {[
                          { label: "Control maturity", value: maturityValue, color: BRAND.primaryDark },
                          { 
  label: "Framework coverage", 
  value: scoring.frameworkCoverage ?? frameworkMappings.length, 
  color: BRAND.secondary 
},
                          { 
  label: "Open gaps", 
  value: scoring.openGaps ?? questions.filter(q => q.response === "GAP").length, 
  color: "#DC2626" 
},
                          { label: "Linked risks", value: riskMappings.length, color: BRAND.gray900 },
                        ].map((s) => (
                          <div key={s.label} style={{
                            background: BRAND.gray50, borderRadius: "8px",
                            padding: "12px 14px", textAlign: "center",
                            border: `1px solid ${BRAND.gray100}`,
                          }}>
                            <div style={{ fontSize: "10px", color: BRAND.gray500, marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.3px", fontWeight: 600 }}>{s.label}</div>
                            <div style={{ fontSize: "22px", fontWeight: 800, color: s.color, lineHeight: 1.1 }}>{s.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ═══ MODALS ═══ */}

         {/* Policy Upload Modal */}
<Modal title={policyForm.parentPolicyId ? `Upload New Version — ${policyForm.policyName}` : "Upload Policy"} show={showPolicyModal} onClose={() => setShowPolicyModal(false)}>
  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>

    {/* Policy Name — editable only for new policies */}
    {!policyForm.parentPolicyId && (
      <input
        placeholder="Policy Name" value={policyForm.policyName} style={inputStyle}
        onChange={(e) => setPolicyForm({ ...policyForm, policyName: e.target.value })}
      />
    )}

    {/* Version */}
    <div style={{ display: "flex", gap: "10px" }}>
      <input
        placeholder="Version (e.g. 1.0)" value={policyForm.version}
        style={{ ...inputStyle, flex: 1 }}
        onChange={(e) => setPolicyForm({ ...policyForm, version: e.target.value })}
      />
      <span style={{
        padding: "9px 14px", borderRadius: "8px", fontSize: "12px",
        background: BRAND.primaryLight, color: BRAND.primaryDark,
        fontWeight: 600, display: "flex", alignItems: "center", whiteSpace: "nowrap",
      }}>
        v{policyForm.version || "1.0"}
      </span>
    </div>

    {/* File Upload — drag & drop */}
    <div>
      <label style={{ fontSize: "12px", fontWeight: 600, color: BRAND.gray600, marginBottom: "4px", display: "block" }}>
        Policy Document
      </label>
      <div
        style={{
          border: `2px dashed ${policyForm.file ? BRAND.primary : BRAND.gray200}`,
          borderRadius: "8px", padding: "16px", textAlign: "center",
          background: policyForm.file ? BRAND.primaryLight : BRAND.gray50,
          cursor: "pointer", transition: "all 0.2s",
        }}
        onClick={() => document.getElementById("policy-file-input").click()}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = BRAND.primary; }}
        onDragLeave={(e) => { e.currentTarget.style.borderColor = BRAND.gray200; }}
        onDrop={(e) => {
          e.preventDefault(); e.currentTarget.style.borderColor = BRAND.gray200;
          const file = e.dataTransfer.files[0];
          if (file) setPolicyForm({ ...policyForm, file });
        }}
      >
        <input
          id="policy-file-input" type="file" style={{ display: "none" }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.txt,.zip"
          onChange={(e) => { const file = e.target.files[0]; if (file) setPolicyForm({ ...policyForm, file }); }}
        />
        {policyForm.file ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            <Upload size={14} style={{ color: BRAND.primaryDark }} />
            <span style={{ fontSize: "13px", fontWeight: 600, color: BRAND.primaryDark }}>{policyForm.file.name}</span>
            <span style={{ fontSize: "11px", color: BRAND.gray500 }}>({(policyForm.file.size / 1024).toFixed(1)} KB)</span>
            <button onClick={(e) => { e.stopPropagation(); setPolicyForm({ ...policyForm, file: null }); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "2px" }}>
              <X size={14} style={{ color: BRAND.gray400 }} />
            </button>
          </div>
        ) : (
          <>
            <Upload size={20} style={{ color: BRAND.gray400, marginBottom: "4px" }} />
            <div style={{ fontSize: "13px", color: BRAND.gray500 }}>
              Drag & drop or <span style={{ color: BRAND.secondary, fontWeight: 600 }}>browse</span>
            </div>
            <div style={{ fontSize: "11px", color: BRAND.gray400, marginTop: "2px" }}>
              PDF, DOC, XLS, PNG, JPG, ZIP — max 10MB
            </div>
          </>
        )}
      </div>
    </div>

    {/* Notes */}
    <textarea
      placeholder="Version notes (optional)" value={policyForm.notes} rows={2}
      style={{ ...inputStyle, resize: "vertical" }}
      onChange={(e) => setPolicyForm({ ...policyForm, notes: e.target.value })}
    />

    {/* Actions */}
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "6px" }}>
      <button onClick={() => setShowPolicyModal(false)} style={btnOutline}>Cancel</button>
      <button
        disabled={!policyForm.policyName || policyUploading}
        onClick={async () => {
          setPolicyUploading(true);
          try {
            let fileUrl, fileKey;
            if (policyForm.file) {
              const region = sessionStorage.getItem("userRegion") || "IN";
              const fd = new FormData();
              fd.append("file", policyForm.file);
              const uploadRes = await axios.post(
                `https://api.srqllabapi.com/trustuploadpolicy/api/trust/control/${meta.controlId}/policy/upload`,
                fd, { headers: { "X-Region": region, "X-Org-Id": profile.orgId } }
              );
              fileUrl = uploadRes.data.file?.fileUrl;
              fileKey = uploadRes.data.file?.fileKey;
              
            await addPolicy(meta.controlId, {
              policyName: policyForm.policyName,
              version: policyForm.version,
              notes: policyForm.notes,
              fileUrl, fileKey,
              uploadedBy: profile?.email,
              parentPolicyId: policyForm.parentPolicyId,
            });
            setShowPolicyModal(false);
            setPolicyForm({ policyName: "", version: "1.0", notes: "", file: null });
            logActivity({
                        userData,
                        orgId:       orgId,
                        region:      sessionStorage.getItem('userRegion') || 'AP',
                        module:      'Global Compliance & Trust',
                        category:    'control',
                        action:      'Policy uploaded',
                        severity:    'success',
                        description: `Policy with ${policyForm.policyName} is uploaded`,});
            }
          } catch (err) {
            console.error("Policy upload failed", err);
          } finally {
            setPolicyUploading(false);
          }
        }}
        style={{ ...btnPrimary, opacity: (!policyForm.policyName || policyUploading) ? 0.5 : 1, cursor: (!policyForm.policyName || policyUploading) ? "not-allowed" : "pointer" }}
      >
        <Upload size={13} /> {policyUploading ? "Uploading..." : policyForm.parentPolicyId ? "Upload Version" : "Upload Policy"}
      </button>
    </div>
  </div>
</Modal>


         {/* Evidence Modal */}
<Modal title="Upload Evidence" show={showEvidenceModal} onClose={() => setShowEvidenceModal(false)}>
  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
    <input placeholder="Evidence Name" value={evidenceForm.evidenceName} style={inputStyle}
      onChange={(e) => setEvidenceForm({ ...evidenceForm, evidenceName: e.target.value })} />

    <select value={evidenceForm.type} style={{ ...inputStyle, cursor: "pointer" }}
      onChange={(e) => setEvidenceForm({ ...evidenceForm, type: e.target.value })}>
      <option value="">Select Type</option>
      <option value="Document">Document</option>
      <option value="Screenshot">Screenshot</option>
      <option value="Export">Export</option>
      <option value="Configuration">Configuration</option>
      <option value="Other">Other</option>
    </select>

    {/* ★ FILE UPLOAD ★ */}
    <div>
      <label style={{ fontSize: "12px", fontWeight: 600, color: BRAND.gray600, marginBottom: "4px", display: "block" }}>
        Attach file
      </label>
      <div
        style={{
          border: `2px dashed ${evidenceForm.file ? BRAND.primary : BRAND.gray200}`,
          borderRadius: "8px", padding: "16px", textAlign: "center",
          background: evidenceForm.file ? BRAND.primaryLight : BRAND.gray50,
          cursor: "pointer", transition: "all 0.2s",
        }}
        onClick={() => document.getElementById("evidence-file-input").click()}
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = BRAND.primary; }}
        onDragLeave={(e) => { e.currentTarget.style.borderColor = BRAND.gray200; }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = BRAND.gray200;
          const file = e.dataTransfer.files[0];
          if (file) setEvidenceForm({ ...evidenceForm, file });
        }}
      >
        <input
          id="evidence-file-input"
          type="file"
          style={{ display: "none" }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.txt,.zip"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file) setEvidenceForm({ ...evidenceForm, file });
          }}
        />
        {evidenceForm.file ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            <Upload size={14} style={{ color: BRAND.primaryDark }} />
            <span style={{ fontSize: "13px", fontWeight: 600, color: BRAND.primaryDark }}>
              {evidenceForm.file.name}
            </span>
            <span style={{ fontSize: "11px", color: BRAND.gray500 }}>
              ({(evidenceForm.file.size / 1024).toFixed(1)} KB)
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); setEvidenceForm({ ...evidenceForm, file: null }); }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: "2px" }}>
              <X size={14} style={{ color: BRAND.gray400 }} />
            </button>
          </div>
        ) : (
          <div>
            <Upload size={20} style={{ color: BRAND.gray400, marginBottom: "4px" }} />
            <div style={{ fontSize: "13px", color: BRAND.gray500 }}>
              Drag & drop or <span style={{ color: BRAND.secondary, fontWeight: 600 }}>browse</span>
            </div>
            <div style={{ fontSize: "11px", color: BRAND.gray400, marginTop: "2px" }}>
              PDF, DOC, XLS, PNG, JPG, ZIP (max 10MB)
            </div>
          </div>
        )}
      </div>
    </div>

    <textarea placeholder="Notes" value={evidenceForm.notes} rows={3}
      style={{ ...inputStyle, resize: "vertical" }}
      onChange={(e) => setEvidenceForm({ ...evidenceForm, notes: e.target.value })} />

    <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "6px" }}>
      <button onClick={() => setShowEvidenceModal(false)} style={btnOutline}>Cancel</button>
    
      <button onClick={async () => {
        let fileUrl = "";
        let fileKey = "";

        if (evidenceForm.file) {
          try {
            const region = sessionStorage.getItem("userRegion") || "IN";
            const formData = new FormData();
            formData.append("file", evidenceForm.file);
             console.log(evidenceForm.file);
            const uploadRes = await axios.post(
              `https://api.srqllabapi.com/trustuploadevidence/api/trust/control/${meta.controlId}/evidence/upload`,
              formData,
              {
                headers: {
                  "X-Region": region,
                  "X-Org-Id": profile.orgId,
                  
                },
              }
            );
            
            fileUrl = uploadRes.data.file.fileUrl;
            fileKey = uploadRes.data.file.fileKey;

            
          } catch (err) {
            console.error("File upload failed:", err);
          }
        }

        await addEvidence(meta.controlId, {
          evidenceName: evidenceForm.evidenceName,
          type: evidenceForm.type,
          notes: evidenceForm.notes,
          fileUrl,
          fileKey,
          uploadedBy: profile?.email || "",
        });
        setShowEvidenceModal(false);
        setEvidenceForm({ evidenceName: "", type: "", notes: "", file: null });
        logActivity({
                        userData,
                        orgId:       orgId,
                        region:      sessionStorage.getItem('userRegion') || 'AP',
                        module:      'Global Compliance & Trust',
                        category:    'control',
                        action:      'Evidence uploaded',
                        severity:    'success',
                        description: `Evidence with ${evidenceForm.evidenceName} is uploaded`,});
            
      }} style={btnPrimary}>
        <Upload size={13} /> Upload
      </button>

    </div>
  </div>
</Modal>


          {/* Risk Modal */}
          <Modal title="Link Risk" show={showRiskModal} onClose={() => setShowRiskModal(false)}>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <input placeholder="Risk ID" value={riskForm.riskId} style={inputStyle} onChange={(e) => setRiskForm({ ...riskForm, riskId: e.target.value })} />
              <input placeholder="Risk Name" value={riskForm.riskName} style={inputStyle} onChange={(e) => setRiskForm({ ...riskForm, riskName: e.target.value })} />
              <select value={riskForm.severity} style={{ ...inputStyle, cursor: "pointer" }} onChange={(e) => setRiskForm({ ...riskForm, severity: e.target.value })}>
                <option value="">Select Severity</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </select>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "6px" }}>
                <button onClick={() => setShowRiskModal(false)} style={btnOutline}>Cancel</button>
                <button onClick={async () => {
  await addRiskMapping(meta.controlId, {
    ...riskForm,
    source: "Manual",
    addedBy: profile?.email,
    addedByUserId: profile?.sub || profile?.userId,
  });
  setShowRiskModal(false);
  setRiskForm({ riskId: "", riskName: "", severity: "" });
}} style={btnPrimary}>Link Risk</button>
              </div>
            </div>
          </Modal>

          {/* ═══ OWNERSHIP MODAL — with user search/picker ═══ */}
         <Modal title="Manage Ownership" show={showOwnerModal} onClose={() => setShowOwnerModal(false)} wide>
  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
    {/* Role selector */}
    <div>
      <label style={{ fontSize: "12px", fontWeight: 600, color: BRAND.gray600, marginBottom: "4px", display: "block" }}>
        Assign as
      </label>
      <select
        value={ownerRole}
        onChange={(e) => setOwnerRole(e.target.value)}
        style={{ ...inputStyle, maxWidth: "220px" }}
      >
        <option value="controlOwner">Control Owner</option>
        <option value="operationalOwner">Operational Owner</option>
        <option value="reviewer">Reviewer</option>
        <option value="contributor">Contributor</option>
      </select>
    </div>
              

              {/* Search bar */}
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: BRAND.gray600, marginBottom: "4px", display: "block" }}>
                  Search user by name or email
                </label>
                <div style={{ position: "relative" }}>
                  <Search size={14} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: BRAND.gray400 }} />
                  <input
                    placeholder="Type to search..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    style={{ ...inputStyle, paddingLeft: "32px" }}
                  />
                </div>
              </div>

              {/* User list */}
              <div style={{
                border: `1px solid ${BRAND.gray200}`, borderRadius: "8px",
                maxHeight: "220px", overflowY: "auto",
              }}>
                {loadingAllUsers ? (
                  <div style={{ padding: "20px", textAlign: "center", color: BRAND.gray400, fontSize: "13px" }}>
                    Loading users...
                  </div>
                ) : filteredUsers.length > 0 ? (
                  filteredUsers.map((u) => {
                    const isSelected = selectedUser?.email === u.email;
                    return (
                      <div key={u.email}
                        onClick={() => setSelectedUser(u)}
                        style={{
                          padding: "10px 14px", cursor: "pointer",
                          display: "flex", alignItems: "center", gap: "10px",
                          borderBottom: `1px solid ${BRAND.gray100}`,
                          background: isSelected ? BRAND.secondaryLight : "transparent",
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = BRAND.gray50; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                      >
                        {/* Avatar */}
                        <div style={{
                          width: "32px", height: "32px", borderRadius: "50%",
                          background: isSelected ? BRAND.secondary : BRAND.gray200,
                          color: isSelected ? BRAND.white : BRAND.gray500,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: "13px", fontWeight: 700, flexShrink: 0,
                        }}>
                          {(u.name || u.email || "?").charAt(0).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: "13px", fontWeight: 600, color: BRAND.gray900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {u.name || u.email}
                          </div>
                          <div style={{ fontSize: "11px", color: BRAND.gray400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {u.email}
                          </div>
                        </div>
                        {isSelected && (
                          <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: BRAND.secondary, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ padding: "20px", textAlign: "center", color: BRAND.gray400, fontSize: "13px" }}>
                    {userSearch ? "No users match your search." : "No domain users found."}
                  </div>
                )}
              </div>

              {/* Selected user preview */}
              {selectedUser && (
                <div style={{
                  padding: "10px 14px", borderRadius: "8px",
                  background: BRAND.primaryLight, border: `1px solid ${BRAND.primary}`,
                  display: "flex", alignItems: "center", gap: "8px", fontSize: "13px",
                }}>
                  <Users size={14} style={{ color: BRAND.primaryDark }} />
                  <span style={{ fontWeight: 600, color: BRAND.gray900 }}>{selectedUser.name || selectedUser.email}</span>
                  <span style={{ color: BRAND.gray500 }}>→</span>
                  <span style={{ color: BRAND.secondary, fontWeight: 500 }}>
                    {ownerRole === "contributor" ? "Contributor" :
                     ownerRole === "controlOwner" ? "Control Owner" :
                     ownerRole === "operationalOwner" ? "Operational Owner" : "Reviewer"}
                  </span>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "4px" }}>
                <button onClick={() => setShowOwnerModal(false)} style={btnOutline}>Cancel</button>
                <button
                  onClick={handleAssignUser}
                  disabled={!selectedUser}
                  style={{
                    ...btnPrimary,
                    opacity: selectedUser ? 1 : 0.5,
                    cursor: selectedUser ? "pointer" : "not-allowed",
                  }}
                >
                  {ownerModalType === "contributor" ? "Add Contributor" : "Assign"}
                </button>
              </div>
            </div>
          </Modal>

          {/* ═══ AUDITOR OBSERVATION MODAL ═══ */}
          <Modal
            title={editingObservation ? "Edit Observation" : "Add Auditor Observation"}
            show={showObservationModal}
            onClose={() => {
              setShowObservationModal(false);
              setEditingObservation(null);
              setObservationForm({ title: "", type: "OBSERVATION", severity: "MEDIUM", status: "OPEN", description: "", recommendation: "" });
            }}
            wide
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {/* Title */}
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: BRAND.gray600, marginBottom: "4px", display: "block" }}>
                  Title <span style={{ color: "#DC2626" }}>*</span>
                </label>
                <input
                  placeholder="Observation title"
                  value={observationForm.title}
                  style={inputStyle}
                  onChange={(e) => setObservationForm({ ...observationForm, title: e.target.value })}
                />
              </div>

              {/* Type & Severity row */}
              <div style={{ display: "flex", gap: "10px" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: BRAND.gray600, marginBottom: "4px", display: "block" }}>
                    Type
                  </label>
                  <select
                    value={observationForm.type}
                    style={{ ...inputStyle, cursor: "pointer" }}
                    onChange={(e) => setObservationForm({ ...observationForm, type: e.target.value })}
                  >
                    <option value="OBSERVATION">Observation</option>
                    <option value="FINDING">Finding</option>
                    <option value="RECOMMENDATION">Recommendation</option>
                    <option value="COMMENT">Comment</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: BRAND.gray600, marginBottom: "4px", display: "block" }}>
                    Severity
                  </label>
                  <select
                    value={observationForm.severity}
                    style={{ ...inputStyle, cursor: "pointer" }}
                    onChange={(e) => setObservationForm({ ...observationForm, severity: e.target.value })}
                  >
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                </div>
              </div>

              {/* Status */}
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: BRAND.gray600, marginBottom: "4px", display: "block" }}>
                  Status
                </label>
                <select
                  value={observationForm.status}
                  style={{ ...inputStyle, cursor: "pointer" }}
                  onChange={(e) => setObservationForm({ ...observationForm, status: e.target.value })}
                >
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="RESOLVED">Resolved</option>
                  <option value="CLOSED">Closed</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: BRAND.gray600, marginBottom: "4px", display: "block" }}>
                  Description
                </label>
                <textarea
                  placeholder="Describe the observation in detail..."
                  value={observationForm.description}
                  rows={4}
                  style={{ ...inputStyle, resize: "vertical" }}
                  onChange={(e) => setObservationForm({ ...observationForm, description: e.target.value })}
                />
              </div>

              {/* Recommendation */}
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: BRAND.gray600, marginBottom: "4px", display: "block" }}>
                  Recommendation <span style={{ fontWeight: 400, color: BRAND.gray400 }}>(optional)</span>
                </label>
                <textarea
                  placeholder="Recommended remediation or action..."
                  value={observationForm.recommendation}
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical" }}
                  onChange={(e) => setObservationForm({ ...observationForm, recommendation: e.target.value })}
                />
              </div>

              {/* Actions */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "6px" }}>
                <button
                  onClick={() => {
                    setShowObservationModal(false);
                    setEditingObservation(null);
                    setObservationForm({ title: "", type: "OBSERVATION", severity: "MEDIUM", status: "OPEN", description: "", recommendation: "" });
                  }}
                  style={btnOutline}
                >
                  Cancel
                </button>
                <button
                  disabled={!observationForm.title}
                  onClick={async () => {
                    if (!observationForm.title) return;
                    try {
                      if (editingObservation) {
                        await updateObservation(meta.controlId, editingObservation, {
                          ...observationForm,
                          updatedBy: profile?.email,
                        });
                      } else {
                        await addObservation(meta.controlId, {
                          ...observationForm,
                          addedBy: profile?.email,
                        });
                      }
                      setShowObservationModal(false);
                      setEditingObservation(null);
                      setObservationForm({ title: "", type: "OBSERVATION", severity: "MEDIUM", status: "OPEN", description: "", recommendation: "" });
                    } catch (err) {
                      console.error("Failed to save observation:", err);
                    }
                  }}
                  style={{
                    ...btnPrimary,
                    opacity: !observationForm.title ? 0.5 : 1,
                    cursor: !observationForm.title ? "not-allowed" : "pointer",
                  }}
                >
                  <CheckCircle size={13} /> {editingObservation ? "Save Changes" : "Add Observation"}
                </button>
              </div>
            </div>
          </Modal>

          <BackgroundFooter isSidebarCollapsed={isSidebarCollapsed} />
        </Col>
      </Col>
    </Row> 
  );
}
 